import { useEffect, useState } from "react";
import {
  BookOpen,
  Users as UsersIcon,
  GraduationCap,
  ClipboardText,
  Trophy,
  Sparkle,
  Student,
} from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const COLORS = ["#1e3a8a", "#dc2626", "#0ea5e9", "#f59e0b"];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6 transition-transform hover:-translate-y-1 hover:shadow-lg">
      <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-lg ${accent ? "bg-accent/10 text-accent" : "bg-primary/10 text-primary"}`}>
        <Icon size={24} weight="duotone" />
      </div>
      <p className="font-display text-3xl font-extrabold tracking-tight">{value}</p>
      <p className="mt-1 text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-muted-foreground">Chargement...</div>;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Tableau de bord</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Bonjour, {user.name.split(" ")[0]} 👋</h1>
      </div>

      {stats.role === "admin" && (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={UsersIcon} label="Utilisateurs" value={stats.users} />
            <Stat icon={Student} label="Étudiants" value={stats.students} />
            <Stat icon={GraduationCap} label="Professeurs" value={stats.teachers} accent />
            <Stat icon={BookOpen} label="Cours publiés" value={stats.published_courses} />
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-bold">Répartition des rôles</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={stats.by_role} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={3}>
                    {stats.by_role.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-4">
                {stats.by_role.map((r, i) => (
                  <span key={r.name} className="flex items-center gap-2 text-sm">
                    <span className="h-3 w-3 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    {r.name} ({r.value})
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-bold">Aperçu de la plateforme</h3>
              <div className="mt-4 space-y-3">
                {[
                  ["Total des cours", stats.courses],
                  ["Groupes", stats.groups],
                  ["Cours publiés", stats.published_courses],
                ].map(([l, v]) => (
                  <div key={l} className="flex items-center justify-between rounded-lg bg-secondary p-4">
                    <span className="text-sm text-muted-foreground">{l}</span>
                    <span className="font-display text-xl font-bold text-primary">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {stats.role === "professeur" && (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={BookOpen} label="Mes cours" value={stats.my_courses} />
            <Stat icon={Student} label="Mes étudiants" value={stats.students} />
            <Stat icon={ClipboardText} label="Copies à corriger" value={stats.pending_grading} accent />
            <Stat icon={Sparkle} label="Quiz créés" value={stats.quizzes} />
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-bold">Étudiants par cours</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.course_stats}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis allowDecimals={false} fontSize={12} />
                <Tooltip />
                <Bar dataKey="étudiants" fill="#1e3a8a" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {stats.role === "etudiant" && (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={BookOpen} label="Cours suivis" value={stats.my_courses} />
            <Stat icon={Trophy} label="Moyenne générale" value={`${stats.average}/20`} accent />
            <Stat icon={ClipboardText} label="Notes reçues" value={stats.graded_items} />
            <Stat icon={Sparkle} label="Quiz passés" value={stats.quiz_attempts} />
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-bold">Évolution de mes notes</h3>
            {stats.grade_progress.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">Pas encore de notes.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={stats.grade_progress}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis domain={[0, 20]} fontSize={12} />
                  <Tooltip />
                  <Line type="monotone" dataKey="note" stroke="#dc2626" strokeWidth={3} dot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}
